/*
  AuditFeed — shows the last N audit events for a given rx_id.
  Hides itself when feature_accounts is off or the user has no family.
*/
import { useEffect, useState } from "react";
import { CheckCircle2, XCircle, Loader2, Activity } from "lucide-react";
import { audit } from "../lib/accountApi";
import { useAuth } from "../contexts/AuthContext";
import { isFeatureEnabled } from "./FeatureGate";

function ActionPill({ action }) {
  const map = {
    dose_taken:   { label: "marked taken",   bg: "var(--success)",  Icon: CheckCircle2 },
    dose_skipped: { label: "marked skipped", bg: "var(--warning)",  Icon: XCircle },
    dose_pending: { label: "reset to pending", bg: "var(--text-muted)", Icon: Activity },
  };
  const def = map[action] || { label: action, bg: "var(--text-muted)", Icon: Activity };
  const Icon = def.Icon;
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded"
          style={{ background: "var(--surface-2)", color: "var(--text-main)" }}>
      <Icon className="h-3 w-3" style={{ color: def.bg }} /> {def.label}
    </span>
  );
}

export default function AuditFeed({ rxId, limit = 20 }) {
  const { user } = useAuth();
  const [enabled, setEnabled] = useState(isFeatureEnabled("feature_accounts"));
  const [events, setEvents] = useState(null);

  useEffect(() => {
    const h = () => setEnabled(isFeatureEnabled("feature_accounts"));
    window.addEventListener("vh-config-loaded", h);
    return () => window.removeEventListener("vh-config-loaded", h);
  }, []);

  useEffect(() => {
    if (!enabled || !user || !rxId) { setEvents(null); return; }
    audit.list({ rx_id: rxId, limit })
      .then(setEvents)
      .catch(() => setEvents([]));
  }, [enabled, user, rxId, limit]);

  if (!enabled || !user || events === null) return null;
  if (events.length === 0) return null;

  return (
    <div className="card-tactile rounded-2xl p-5" data-testid="audit-feed">
      <div className="flex items-center gap-2 mb-3">
        <Activity className="h-4 w-4" style={{ color: "var(--text-muted)" }} />
        <h3 className="font-display tracking-tight text-lg">Family activity</h3>
      </div>
      <ol className="space-y-2.5">
        {events.map(ev => {
          const who = ev.actor_name || ev.actor_email || "Someone";
          const when = ev.at ? new Date(ev.at).toLocaleString() : "";
          return (
            <li key={ev.id} data-testid={`audit-event-${ev.id}`} className="flex items-start gap-2 text-sm">
              <div className="h-7 w-7 rounded-full flex items-center justify-center text-[11px] font-semibold text-white shrink-0"
                   style={{ background: "var(--primary)" }}>
                {who.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div>
                  <strong>{who}</strong> <ActionPill action={ev.action} />
                </div>
                <div className="text-xs" style={{ color: "var(--text-muted)" }}>{when}</div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
